import { Component, OnInit, NgZone, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from 'ng2-translate/ng2-translate';

import { MapsService } from '../maps';
import { NotificationClient } from '../shared/notifications/notification-client';
import { NotificationModel, NotificationEvent } from '../shared/notifications/notification.model';
import { AuthService } from './services/auth.service';
import { UserData } from '../domain/models/userData';
import { UserRole } from '../domain/enums/userRole';

import * as _ from 'lodash';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss']
})
export class AuthComponent implements OnInit, OnDestroy {
  public showTrafficLayer: boolean;
  public showAllRoutesLayer: boolean;
  public showAllRoutesButton: boolean;
  public currentNotification: NotificationModel;
  public isImpersonated: boolean;
  public userRoles: UserRole[];
  public userName: string;

  private sidebarShown: boolean;
  private language: string;
  private notificationQueue: NotificationModel[];

  constructor(private router: Router,
    private mapsService: MapsService,
    private translateService: TranslateService,
    private authService: AuthService,
    private notificationClient: NotificationClient,
    private zone: NgZone) {
  }

  ngOnInit() {
    this.sidebarShown = true;
    let show = localStorage.getItem('showAllRoutesButton');
    this.showAllRoutesButton = show === 'true';
    this.showTrafficLayer = true;
    this.showAllRoutesLayer = false;
    this.notificationQueue = [];
    this.getUserSettings();
    this.initializeNotificationClient();
    this.authService.clearDraftRoutes();

    this.userRoles = [];
    let decodedJwt = this.authService.getDecodedJwtToken();
    this.isImpersonated = this.authService.checkImpersonation();
    if (decodedJwt) {
      let uR = decodedJwt ? [].concat(decodedJwt['role']) : [];
      this.userRoles = uR.map((role: string) => UserRole[role]);
      this.userName = decodedJwt.name;
    }

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        if (event.url.endsWith('auth')) {
          this.mapsService.resetMap();
        }
        setTimeout(() => {
          this.mapsService.resizeMap();
        }, 1000);
      }
    });
  }

  ngOnDestroy() {
    this.notificationClient.closeConnection();
  }

  getUserSettings(): void {
    this.authService.getUserSettings().then((userData: UserData) => {
      if (userData.language) {
        this.language = userData.language;
        this.translateService.use(userData.language);
      }
    });
  }

  languageChanged(language: string) {
    this.language = language;
    this.updateUserSettings();
  }

  updateUserSettings(): void {
    let userdata = new UserData();
    userdata.language = this.language;
    this.authService.updateUserSettings(userdata);
  }

  resizeMap(): void {
    this.mapsService.resizeMap();
  }

  toggleSidebar(): void {
    this.sidebarShown = !this.sidebarShown;
  }

  showSidebar(): boolean {
    return this.sidebarShown;
  }

  navigateTo(route: string) {
    switch (route) {
      case 'overview':
        this.router.navigate(['auth']);
        localStorage.setItem('showAllRoutesButton', 'false');
        this.showAllRoutesButton = false;
        break;
      case 'new':
        this.router.navigate(['auth/new-route']);
        break;
      case 'projects':
        this.router.navigate(['auth/projects']);
        localStorage.setItem('showAllRoutesButton', 'true');
        this.showAllRoutesButton = true;
        break;
      case 'dashboards':
        this.router.navigate(['auth/dashboards']);
        localStorage.setItem('showAllRoutesButton', 'false');
        this.showAllRoutesButton = false;
        break;
      case 'admin':
        this.router.navigate(['admin']);
        localStorage.setItem('showAllRoutesButton', 'false');
        this.showAllRoutesButton = false;
        break;
    }
    this.mapsService.clearRoute();
  }

  isDetailPage(): boolean {
    return this.router.url.indexOf('auth/route-detail') !== -1;
  }

  toggleTrafficLayer(): void {
    this.zone.run(() => {
      this.mapsService.toggleTrafficLayer(this.showTrafficLayer);
    });
  }
  toggleAllRoutesLayer(): void {
    this.zone.run(() => {
      this.mapsService.toggleAllRoutesLayer(this.showAllRoutesLayer);
    });
  }

  clearNotification(): void {
    if (this.notificationQueue.length > 0) {
      this.notificationQueue.splice(0, 1);
    }
    this.showNextNotification();
  }

  isNotificationVisible(): boolean {
    return this.notificationQueue.length > 0;
  }

  clearAllNotifications(): void {
    this.notificationQueue = [];
  }

  private showNextNotification(): void {
    if (this.notificationQueue.length > 0) {
      this.currentNotification = this.notificationQueue[0];
    }
  }

  private initializeNotificationClient(): void {
    this.notificationClient.startConnection();

    this.notificationClient.eventReceived.subscribe((event: NotificationEvent) => {
      this.notificationQueue.push(event.messages[this.translateService.currentLang]);
      this.showNextNotification();
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/auth/auth.component.ts